import { ptsToPxls, pxlsToPts } from './pageLayoutMath';

/**
 * Compute the next accumulated maxHeight boundary value, updating the shared
 * `boundaries` and `addedManuals` arrays in place (mutation contract preserved
 * to match the original inner-function behaviour).
 *
 * Call this whenever `actualHeight > maxHeight`, then store the return value
 * back into `maxHeight`.
 *
 * @param {{
 *   height: number,            - current accumulated actualHeight (px)
 *   index: number,             - index of the current editor child
 *   maxHeight: number,         - current page-boundary ceiling (px)
 *   boundaries: Array,         - mutable boundary list (pushed into here)
 *   addedManuals: Array,       - mutable list of manuals already added
 *   manualBoundaries: Array,   - full list of manual page breaks
 *   basePageHeightInPts: number,
 *   baseFirstPageHeightInPts: number | null,
 *   baseLastPageHeightInPts: number | null,
 *   baseFirstAndLastPageHeightInPts: number | null,
 *   firstPageMargins: object | null | undefined,
 *   lastPageMargins: object | null | undefined,
 *   editorChildren: Array,     - editor.children (read-only)
 * }}
 * @returns {number} new maxHeight value
 */
export function computeNextMaxHeight({
	height,
	index,
	maxHeight,
	boundaries,
	addedManuals,
	manualBoundaries,
	basePageHeightInPts,
	baseFirstPageHeightInPts,
	baseLastPageHeightInPts,
	baseFirstAndLastPageHeightInPts,
	firstPageMargins,
	lastPageMargins,
	editorChildren,
}) {
	if (height === 0) {
		if (firstPageMargins && lastPageMargins) {
			if (manualBoundaries.length > 0 && manualBoundaries[0].pos < baseFirstAndLastPageHeightInPts) {
				boundaries.push(manualBoundaries[0]);
				addedManuals.push(manualBoundaries[0]);
				return +ptsToPxls(manualBoundaries[0].pos).toFixed(2);
			} else {
				const newPos = { pos: baseFirstAndLastPageHeightInPts };
				boundaries.push(newPos);
				return ptsToPxls(baseFirstAndLastPageHeightInPts);
			}
		} else if (firstPageMargins) {
			if (manualBoundaries.length > 0 && manualBoundaries[0].pos < baseFirstPageHeightInPts) {
				boundaries.push(manualBoundaries[0]);
				addedManuals.push(manualBoundaries[0]);
				return +ptsToPxls(manualBoundaries[0].pos).toFixed(2);
			} else {
				const newPos = { pos: baseFirstPageHeightInPts };
				boundaries.push(newPos);
				return ptsToPxls(baseFirstPageHeightInPts);
			}
		} else if (lastPageMargins) {
			if (manualBoundaries.length > 0 && manualBoundaries[0].pos < baseLastPageHeightInPts) {
				boundaries.push(manualBoundaries[0]);
				addedManuals.push(manualBoundaries[0]);
				return +ptsToPxls(manualBoundaries[0].pos).toFixed(2);
			} else {
				const newPos = { pos: baseLastPageHeightInPts };
				boundaries.push(newPos);
				return ptsToPxls(baseLastPageHeightInPts);
			}
		} else {
			if (manualBoundaries.length > 0 && manualBoundaries[0].pos < basePageHeightInPts) {
				boundaries.push(manualBoundaries[0]);
				addedManuals.push(manualBoundaries[0]);
				return +ptsToPxls(manualBoundaries[0].pos).toFixed(2);
			} else {
				const newPos = { pos: basePageHeightInPts };
				boundaries.push(newPos);
				return ptsToPxls(basePageHeightInPts);
			}
		}

	} else if (height <= maxHeight) {
		return maxHeight;

	} else {
		if (manualBoundaries.length > addedManuals.length) {
			const nextManual = addedManuals.length > 0 ? manualBoundaries[addedManuals.length] : manualBoundaries[0];
			if (nextManual.pos > pxlsToPts(maxHeight)) {
				if (lastPageMargins) {
					if (nextManual.pos <= baseLastPageHeightInPts + pxlsToPts(maxHeight)) {
						boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index] };
						boundaries.push(nextManual);
						addedManuals.push(nextManual);
						return +ptsToPxls(nextManual.pos).toFixed(2);

					} else if (height <= maxHeight + ptsToPxls(baseLastPageHeightInPts)) {
						const newPos = +(pxlsToPts(maxHeight) + baseLastPageHeightInPts).toFixed(2);
						const newHeight = maxHeight + ptsToPxls(baseLastPageHeightInPts);
						boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index] };
						boundaries.push({ pos: newPos });
						return newHeight;

					} else {
						const prevPos = boundaries.length > 0 ? boundaries[boundaries.length - 1].pos : 0;
						const newPos = prevPos + basePageHeightInPts;
						const newHeight = boundaries.length > 0 ? ptsToPxls(boundaries[boundaries.length - 1].pos) + ptsToPxls(basePageHeightInPts) : 0;
						if (boundaries.length > 0) boundaries[boundaries.length - 1] = boundaries.length === 1 ? { ...boundaries[boundaries.length - 1], at: [index], pos: prevPos } : { ...boundaries[boundaries.length - 1], at: [index] };
						boundaries.push({ pos: newPos });
						return newHeight;
					}

				} else {
					if (nextManual.pos <= basePageHeightInPts + pxlsToPts(maxHeight)) {
						boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index] };
						boundaries.push(nextManual);
						addedManuals.push(nextManual);
						return +ptsToPxls(nextManual.pos).toFixed(2);

					} else {
						const newPos = +(pxlsToPts(maxHeight) + basePageHeightInPts).toFixed(2);
						const newHeight = maxHeight + ptsToPxls(basePageHeightInPts);
						boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index] };
						boundaries.push({ pos: newPos });
						return newHeight;
					}
				}
			}

		} else if (manualBoundaries.length === 0 || (addedManuals.length === manualBoundaries.length)) {
			// no more manual boundaries to check
			if (lastPageMargins && height <= maxHeight + ptsToPxls(baseLastPageHeightInPts)) {
				const newPos = +(pxlsToPts(maxHeight) + baseLastPageHeightInPts).toFixed(2);
				const newHeight = maxHeight + ptsToPxls(baseLastPageHeightInPts);
				boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index] };
				boundaries.push({ pos: newPos });
				return newHeight;

			} else {
				const prevPos = boundaries.length > 0 ? boundaries[boundaries.length - 1].pos : 0;
				if (prevPos > height) {
					return ptsToPxls(prevPos);
				} else {
					if (index === editorChildren.length - 1 && editorChildren[index].type === 'footnote-wrapper') {
						return ptsToPxls(prevPos);
					} else {
						const newPos = prevPos + basePageHeightInPts;
						const newHeight = boundaries.length > 0 ? ptsToPxls(boundaries[boundaries.length - 1].pos) + ptsToPxls(basePageHeightInPts) : 0;
						if (boundaries.length > 0) {
							if (boundaries.length === 1 ) {
								boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index], pos: prevPos };
							} else if (boundaries[boundaries.length - 1] && !boundaries[boundaries.length - 1].manual) {
								boundaries[boundaries.length - 1] = { ...boundaries[boundaries.length - 1], at: [index] };
							}
						}
						boundaries.push({ pos: newPos });
						return newHeight;
					}
				}
			}
		}
	}
}
